class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.abortController = new AbortController();

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', () => {
        if (this.isOpen) this.setResultsHeight();
      });
    }
    document.addEventListener('click', (event) => {
      if (this.isOpen && !this.contains(event.target)) this.close();
    });
    this.addEventListener('click', (event) => {
      if (!event.target.closest('[data-predictive-search-close]')) return;
      this.close();
      this.input.blur();
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  // A/B test: in the Luigi's Box variant its own autocomplete handles the field
  luigiActive() {
    return !!document.querySelector('.luigi-ac');
  }

  onChange() {
    if (this.luigiActive()) return;
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.showStart();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    if (this.luigiActive()) return;
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.showStart();
      return;
    }

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    // On mobile, dismissing the keyboard blurs the input; keep results open (closed via X or outside tap)
    if (window.matchMedia('(max-width: 749px)').matches) return;

    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (this.luigiActive()) return;
    if (!this.getQuery().length && event.code !== 'Escape') this.showStart();
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        this.close();
        this.input.blur();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    // Cancel the request for the previous term so its late response can't overwrite these results
    this.abortController.abort();
    this.abortController = new AbortController();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[limit]')}=10&${encodeURIComponent('resources[limit_scope]')}=each&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  showStart() {
    // The start popup is the predictive-search section rendered without a phrase; fetched once
    if (this.startMarkup === undefined) {
      this.startMarkup = null;
      fetch(`${routes.root_url}?section_id=predictive-search`)
        .then((response) => response.text())
        .then((text) => {
          const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
          this.startMarkup = section && section.querySelector('[data-predictive-search-start]') ? section.innerHTML : '';
          if (!this.getQuery().length && this.contains(document.activeElement)) this.showStart();
        });
      return;
    }

    if (!this.startMarkup) {
      this.close(true);
      return;
    }

    if (!this.predictiveSearchResults.querySelector('[data-predictive-search-start]')) {
      this.predictiveSearchResults.innerHTML = this.startMarkup;
    }
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.removeAttribute('results');
    this.removeAttribute('loading');
    this.open();
  }

  renderSearchResults(resultsMarkup) {
    // The input may have been cleared while the request was in flight
    if (!this.getQuery().length) return;

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;

    if (window.innerWidth < 750) {
      this.resultsMaxHeight = viewportHeight - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
    } else {
      // On desktop the popup starts right under the search field, above the category nav,
      // so measure from the popup itself rather than the bottom of the whole header section
      const bottomMargin = 16;
      this.predictiveSearchResults.style.maxHeight = '';
      this.resultsMaxHeight = viewportHeight - this.predictiveSearchResults.getBoundingClientRect().top - bottomMargin;
    }

    return this.resultsMaxHeight;
  }

  setResultsHeight() {
    const height = `${this.getResultsMaxHeight()}px`;
    // On desktop this is a safety net: the popup only scrolls if it still doesn't
    // fit the viewport after the per-column caps in the CSS kick in.
    this.predictiveSearchResults.style.maxHeight = height;
    // On mobile the popup fills the space below the header down to the bottom of the visible viewport
    if (window.innerWidth < 750) this.predictiveSearchResults.style.height = height;
  }

  open() {
    // Set `open` first so the popup is displayed and its position can be measured
    this.setAttribute('open', true);
    this.setResultsHeight();
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
